/**
 * cv-attribution.js — 2026-08-03
 *
 * Why: `_source` on an order only says WHICH WIDGET was used (page:home,
 * article:inline-early, precheck…). It never says where the visitor came from,
 * so a Meta-ads sale and a direct visit both land as "precheck".
 *
 * This captures the FIRST touch (ad click ids, utm_*, referrer) once per
 * visitor, keeps it for 30 days, and stamps two readable line-item properties
 * on every /cart/add form:
 *
 *   _origine  — traffic channel, e.g. "Meta Ads", "Google (organique)", "Direct"
 *   _outil    — on-site tool that produced the order, e.g. "VIN checker"
 *   _campagne — utm_campaign, only when there is one
 *
 * Underscore-prefixed properties stay hidden from the customer and visible in
 * the Shopify admin. Injection happens in the DOM (not on submit) because the
 * plate forms are hijacked by cv-v2-plate.js and re-submitted elsewhere, and
 * because a programmatic form.submit() would skip any submit listener.
 *
 * Delivery-safe: the pipeline reads the plate by property NAME
 * ('immatriculation' in name.lower()), so extra properties are inert.
 */
(function () {
  'use strict';

  var KEY = 'cv_attr';
  var TTL_MS = 30 * 24 * 3600 * 1000;

  function store(obj) {
    try { localStorage.setItem(KEY, JSON.stringify(obj)); } catch (e) {}
  }
  function load() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (raw && raw.ts && Date.now() - raw.ts < TTL_MS) return raw;
    } catch (e) {}
    return null;
  }

  function host(url) {
    try { return new URL(url).hostname.replace(/^www\./, ''); } catch (e) { return ''; }
  }

  /* Traffic channel from ad click ids > utm > referrer. Ad click ids win:
     they survive when a campaign forgets its utm tags. */
  function detect(params, referrer) {
    var p = function (k) { return (params.get(k) || '').toLowerCase(); };
    var us = p('utm_source');
    var um = p('utm_medium');

    if (params.get('fbclid') || /facebook|instagram|\big\b|\bfb\b|meta/.test(us)) return 'Meta Ads';
    if (params.get('sccid') || /snapchat|snap/.test(us)) return 'Snapchat Ads';
    if (params.get('ttclid') || /tiktok/.test(us)) return 'TikTok Ads';
    if (params.get('gclid') || params.get('gbraid') || params.get('wbraid')) return 'Google Ads';
    if (params.get('msclkid')) return 'Microsoft Ads';
    if (us === 'google' && /cpc|ppc|paid/.test(um)) return 'Google Ads';
    if (/email|mail/.test(um) || /resend|relance|rescue|winback|retarget/.test(us)) return 'Email relance';
    if (us) return 'Campagne : ' + us.slice(0, 40);

    var h = host(referrer);
    if (!h || h === 'carverif.fr') return null;           // internal / no referrer
    if (/(^|\.)google\./.test(h)) return 'Google (organique)';
    if (/bing\./.test(h)) return 'Bing (organique)';
    if (/duckduckgo|ecosia|qwant|yahoo|brave/.test(h)) return h.split('.')[0] + ' (organique)';
    if (/chatgpt|openai|perplexity|claude\.ai|copilot|gemini/.test(h)) return 'IA : ' + h;
    if (/facebook|instagram/.test(h)) return 'Meta (organique)';
    if (/leboncoin|lacentrale|caradisiac|autoplus|largus/.test(h)) return 'Référent auto : ' + h;
    return 'Référent : ' + h;
  }

  function capture() {
    var params;
    try { params = new URLSearchParams(location.search); } catch (e) { return load(); }

    var found = detect(params, document.referrer || '');
    var existing = load();

    // First touch wins, except when the stored one is the "Direct" fallback and
    // we can now name a real channel.
    if (existing && existing.origine && existing.origine !== 'Direct' ) return existing;
    if (!found && existing) return existing;

    var rec = {
      origine: found || (existing && existing.origine) || 'Direct',
      campagne: (params.get('utm_campaign') || '').slice(0, 60),
      // Keep the ORIGINAL landing page when we are only re-labelling a
      // previously-"Direct" visit: the first page seen is the assist signal.
      landing: (existing && existing.landing) || (location.pathname || '').slice(0, 120),
      ts: (existing && existing.ts) || Date.now()
    };
    store(rec);
    return rec;
  }

  var TOOLS = [
    [/^vin-plaque/, 'VIN checker'],
    [/^precheck$/, 'Précheck (arrivée directe)'],
    [/^page:home$/, 'Widget accueil'],
    [/^page:products$/, 'Page produit'],
    [/^page:blogs$/, 'Index blog'],
    [/^article:/, 'Article fiabilité'],
    [/^conseils:/, 'Article conseils'],
    [/^obd:/, 'Page code défaut'],
    [/^modele:/, 'Fiche modèle'],
    [/^hub:/, 'Hub marque'],
    [/^cart/, 'Panier']
  ];

  function toolLabel(src) {
    if (!src) return 'Widget plaque';
    for (var i = 0; i < TOOLS.length; i++) {
      if (TOOLS[i][0].test(src)) return TOOLS[i][1];
    }
    return src;
  }

  function setProp(form, name, value) {
    if (!value) return;
    var sel = 'input[name="properties[' + name + ']"]';
    var el = form.querySelector(sel);
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = 'properties[' + name + ']';
      form.appendChild(el);
    }
    el.value = value;
  }

  function stamp(attr) {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var f = forms[i];
      var srcEl = f.querySelector('input[name="properties[_source]"]');
      var src = srcEl ? srcEl.value : '';
      setProp(f, '_origine', attr.origine);
      setProp(f, '_outil', toolLabel(src));
      // First page of the visit. This is what finally answers "do the 12 500 OBD
      // pages assist sales?" — they convert ~0 on last touch, but nothing ever
      // measured them as an entry point.
      setProp(f, '_entree', attr.landing);
      if (attr.campagne) setProp(f, '_campagne', attr.campagne);
    }
  }

  function init() {
    var attr = capture() || { origine: 'Direct', ts: Date.now() };
    window.cvAttr = attr;
    stamp(attr);
    // The precheck page builds its buy form after the lookup resolves, and the
    // VIN page renders its upsell form on demand — re-stamp on DOM changes.
    if (window.MutationObserver) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        setTimeout(function () { pending = false; stamp(window.cvAttr || attr); }, 150);
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
